import { useEffect, useState, type KeyboardEvent } from 'react'
import { TESTIMONIALS_CAROUSEL, TESTIMONIALS_KEYBOARD } from '../constants/home'
import {
  isTrackSlotVisible,
  minWidthQuery,
  resolveVisibleCount,
  visibleTrackStart,
} from '../helpers/carousel'
import { useAutoCarousel } from './useAutoCarousel'

function useMediaMinWidth(minPx: number): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia(minWidthQuery(minPx)).matches,
  )

  useEffect(() => {
    const query = window.matchMedia(minWidthQuery(minPx))

    function sync() {
      setMatches(query.matches)
    }

    sync()
    query.addEventListener('change', sync)
    return () => query.removeEventListener('change', sync)
  }, [minPx])

  return matches
}

export function useTestimonialsCarousel(slideCount: number) {
  const carousel = useAutoCarousel(slideCount, TESTIMONIALS_CAROUSEL.autoplayMs)
  const isDesktop = useMediaMinWidth(TESTIMONIALS_CAROUSEL.desktopMinPx)
  const isTablet = useMediaMinWidth(TESTIMONIALS_CAROUSEL.tabletMinPx)
  const breakpointVisible = isDesktop
    ? TESTIMONIALS_CAROUSEL.visibleDesktop
    : isTablet
      ? TESTIMONIALS_CAROUSEL.visibleTablet
      : TESTIMONIALS_CAROUSEL.visibleMobile
  const visibleCount = resolveVisibleCount(slideCount, breakpointVisible)
  const trackStart = visibleTrackStart(carousel.activeIndex, slideCount, visibleCount)

  function isVisible(index: number) {
    return isTrackSlotVisible(index, trackStart, visibleCount)
  }

  function onKeyDown(event: KeyboardEvent<HTMLElement>) {
    if (!carousel.canNavigate) return
    if (event.key === TESTIMONIALS_KEYBOARD.prev) {
      event.preventDefault()
      carousel.goPrev()
      return
    }
    if (event.key === TESTIMONIALS_KEYBOARD.next) {
      event.preventDefault()
      carousel.goNext()
    }
  }

  return { ...carousel, trackStart, visibleCount, isVisible, onKeyDown }
}
